import React, { useState } from 'react';
import { useFlashMessage, Button, Select, SelectChangeEvent } from '@indigo-ag/component-module';
import { AgCampaign, AgCreateSacksHectaresInput, FinancingState } from '@indigo-ag/schema';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import i18next from 'core/i18n';
import { EnhancedPaper, InputNumberMask, utils } from 'shared';
import { allCampaigns } from 'features/admin/adminQueries';
import { financingStates } from 'features/brazil-grower/queries';
import { createSacksHectares, getAllFinancingSacksHectares } from 'features/pricing/queries';

export const AddNewPricing: React.FunctionComponent = () => {
  /**
   * State
   */
  const getInitialHectaresInfo = () => ({
    stateId: undefined,
    campaignId: undefined,
    pricePerBag: undefined,
    bagsPerHectares: undefined
  });
  const [sacksHectaresNewInfo, setSacksHectaresNewInfo] = useState<Partial<AgCreateSacksHectaresInput>>(
    getInitialHectaresInfo()
  );
  const [isAddingSacksHectares, setIsAddingSacksHectares] = useState(false);

  /**
   * Queries
   */
  const { data: statesData } = useQuery<{ financingStates: FinancingState[] }>(financingStates);
  const { data: campaignsData } = useQuery<{ financingCampaigns: AgCampaign[] }>(allCampaigns);

  /**
   * Mutations
   */
  const [createSacksHectaresFn] = useMutation(createSacksHectares);

  const updateSacksHectaresNumberValues = (
    key: keyof Pick<AgCreateSacksHectaresInput, 'pricePerBag' | 'bagsPerHectares'>
  ) => {
    return (n: number | null) => {
      setSacksHectaresNewInfo({ ...sacksHectaresNewInfo, [key]: n ? Number(n) : undefined });
    };
  };

  const updateSacksHectaresSelectValues = (key: keyof Pick<AgCreateSacksHectaresInput, 'stateId' | 'campaignId'>) => {
    return (event: SelectChangeEvent<string>): void => {
      setSacksHectaresNewInfo({ ...sacksHectaresNewInfo, [key]: Number(event.target.value) });
    };
  };

  const { addFlashMessage } = useFlashMessage();

  const addSacksHectares = () => {
    setIsAddingSacksHectares(true);

    createSacksHectaresFn({
      variables: {
        sacksHectaresNewInfo
      },
      refetchQueries: [{ query: getAllFinancingSacksHectares }]
    })
      .then(() => {
        setSacksHectaresNewInfo(getInitialHectaresInfo());
        addFlashMessage({
          content: utils.text.firstLetterUppercase(i18next.t('pricingInformationAdded')),
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0].message,
          title: '',
          variant: 'error'
        });
      })
      .finally(() => setIsAddingSacksHectares(false));
  };

  return (
    <EnhancedPaper
      style={{
        marginBottom: '20px',
        paddingBottom: '7px',
        boxShadow: '0px 2px 4px rgba(0, 87, 125, 0.14), 0px 2px 2px rgba(0, 87, 125, 0.1)'
      }}
    >
      <div
        style={{
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <div className="mr-4 inline-block">
          <Select
            placeholder={i18next.t('pleaseSelect')}
            label={i18next.t('state')}
            value={sacksHectaresNewInfo.stateId ? sacksHectaresNewInfo.stateId.toString() : null}
            name="stateId"
            options={
              statesData ? statesData.financingStates.map(s => ({ name: `${s.name} (${s.code})`, value: s.id })) : []
            }
            onChange={updateSacksHectaresSelectValues('stateId')}
            sizeVariant="slim"
          />
        </div>
        <div className="mr-4 inline-block">
          <Select
            placeholder={i18next.t('pleaseSelect')}
            label={i18next.t('campaign')}
            value={sacksHectaresNewInfo.campaignId ? sacksHectaresNewInfo.campaignId.toString() : null}
            name="campaignId"
            options={
              campaignsData ? campaignsData.financingCampaigns.map(c => ({ name: c.name, value: c.id.toString() })) : []
            }
            onChange={updateSacksHectaresSelectValues('campaignId')}
            sizeVariant="slim"
          />
        </div>
        <div className="mr-4 inline-block">
          <InputNumberMask
            label={i18next.t('bagsPriceBase')}
            onNumberChange={updateSacksHectaresNumberValues('pricePerBag')}
            numberValue={sacksHectaresNewInfo.pricePerBag}
            nullable
          />
        </div>
        <div className="mr-4 inline-block">
          <InputNumberMask
            label={i18next.t('bagsBase')}
            onNumberChange={updateSacksHectaresNumberValues('bagsPerHectares')}
            numberValue={sacksHectaresNewInfo.bagsPerHectares}
            nullable
          />
        </div>
        <div className="inline-block">
          <Button
            type="button"
            size="small"
            disabled={!sacksHectaresNewInfo.stateId || !sacksHectaresNewInfo.campaignId}
            onClick={addSacksHectares}
            busy={isAddingSacksHectares}
          >
            {i18next.t('add')}
          </Button>
        </div>
      </div>
    </EnhancedPaper>
  );
};
